// API endpoints
import { auth } from './firebase';
const CREATE_CARD_API = 'https://createlunchcard-bifjdpqtsa-ew.a.run.app';
const GET_CARD_API = 'https://europe-west1-dagens-l-tt.cloudfunctions.net/getLunchCard';
const SMS_API_URL = 'https://sendsms-bifjdpqtsa-ew.a.run.app';

const GET_ALL_CARDS_API = 'https://europe-west1-dagens-l-tt.cloudfunctions.net/getLunchCards';
const DECREMENT_CARD_API = 'https://europe-west1-dagens-l-tt.cloudfunctions.net/decrementLunchCard';

// Demo phone numbers that should always work
export const DEMO_PHONE_NUMBERS = ['0706349637', '0702011393'];

export interface LunchCardAPI {
  id: string;
  phoneNumber: string;
  purchaseDate: string;
  remaining: number;
  // Company-specific fields (optional)
  companyName?: string;
  contactPerson?: string;
  email?: string;
  reference?: string;
  total?: number;
  paymentMethod?: 'invoice' | 'card';
}

interface CreateCardResponse {
  id: string;
  phoneNumber: string;
  purchaseDate: string;
  remaining: string;
}

interface SendSmsResult {
  success: boolean;
  error?: string;
}

// Create a demo card for testing
export const createDemoCard = (phoneNumber: string, id?: string): LunchCardAPI => ({
  id: id || `demo-${phoneNumber}-${Date.now()}`,
  phoneNumber,
  purchaseDate: new Date().toISOString(),
  remaining: 10,
});

// Create a demo company card for testing
export const createDemoCompanyCard = (phoneNumber: string, id?: string): LunchCardAPI => ({
  id: id || `demo-company-${phoneNumber}-${Date.now()}`,
  phoneNumber,
  purchaseDate: new Date().toISOString(),
  remaining: 50,
  total: 50,
  companyName: 'Demo Företag AB',
  contactPerson: 'Demo Person',
  email: 'demo@foretag.se',
});

/**
 * Create a new lunch card via API
 */
export const createLunchCard = async (phoneNumber: string): Promise<LunchCardAPI> => {
  console.log('DEBUG: createLunchCard service called with:', phoneNumber);
  try {
    const user = auth.currentUser;
    const token = user ? await user.getIdToken() : '';
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    console.log('DEBUG: Fetching from:', CREATE_CARD_API);
    const response = await fetch(CREATE_CARD_API, {
      method: 'POST',
      headers,
      body: JSON.stringify({ phoneNumber }),
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const data: CreateCardResponse = await response.json();

    return {
      id: data.id,
      phoneNumber: data.phoneNumber,
      purchaseDate: data.purchaseDate,
      remaining: parseInt(data.remaining, 10),
    };
  } catch (error) {
    console.error('Failed to create lunch card:', error);

    // Fallback: create demo card for testing
    if (DEMO_PHONE_NUMBERS.includes(phoneNumber.replace(/[\s\-]/g, ''))) {
      console.log('Using demo card fallback');
      return createDemoCard(phoneNumber.replace(/[\s\-]/g, ''));
    }

    throw error;
  }
};

// API endpoint for fetching all cards (used by Kassa)
// Bytades till DECREMENT_CARD_API högre upp

/**
 * Fetch all lunch cards from API (used by Kassa)
 */
export const fetchAllLunchCards = async (): Promise<LunchCardAPI[]> => {
  try {
    const user = auth.currentUser;
    const token = user ? await user.getIdToken() : '';
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(GET_ALL_CARDS_API, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();

    // The API returns { lunchCards: [...] }
    const cards = (data.lunchCards || data) as LunchCardAPI[];

    // Normalize remaining to number if it comes as string
    return cards.map(card => ({
      ...card,
      remaining: typeof card.remaining === 'string' ? parseInt(card.remaining, 10) : card.remaining,
      total: card.total ? (typeof card.total === 'string' ? parseInt(card.total as unknown as string, 10) : card.total) : undefined,
    }));
  } catch (error) {
    console.error('Failed to fetch lunch cards:', error);

    // Fallback: return demo cards for testing
    console.log('Using demo cards fallback');
    return [
      createDemoCard('0706349637', 'demo-private-0706349637'),
      createDemoCard('0702011393', 'demo-private-0702011393'),
      createDemoCompanyCard('0706349637', 'demo-company-0706349637'),
    ];
  }
};

/**
 * Fetch a single lunch card by ID from API
 */
export const fetchLunchCardById = async (id: string): Promise<LunchCardAPI | null> => {
  try {
    const response = await fetch(`${GET_CARD_API}?id=${encodeURIComponent(id)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();

    return {
      ...data,
      remaining: typeof data.remaining === 'string' ? parseInt(data.remaining, 10) : data.remaining,
      total: data.total ? (typeof data.total === 'string' ? parseInt(data.total, 10) : data.total) : undefined,
    };
  } catch (error) {
    console.error('Failed to fetch lunch card by ID:', error);
    
    // Demo fallback for specific IDs
    if (id.startsWith('demo-')) {
      if (id.includes('company')) {
        return createDemoCompanyCard('0706349637', id);
      }
      return createDemoCard('0706349637', id);
    }
    
    return null;
  }
};

/**
 * Find a card by ID in the provided list
 */
export const findCardById = (cards: LunchCardAPI[], id: string): LunchCardAPI | undefined => {
  return cards.find(card => card.id === id);
};

/**
 * Find a card by phone number in the provided list
 */
export const findCardByPhone = (cards: LunchCardAPI[], phone: string): LunchCardAPI | undefined => {
  const normalized = phone.replace(/[\s\-]/g, '');
  return cards.find(card => card.phoneNumber === normalized || card.phoneNumber.replace(/[\s\-]/g, '') === normalized);
};

/**
 * Generate QR code data with card ID
 */
export const generateQRDataWithId = (cardId: string, type: 'private' | 'company' = 'private'): string => {
  const prefix = type === 'company' ? 'COMPANYCARD' : 'LUNCHCARD';
  return `${prefix}|${cardId}`;
};

/**
 * Generate the URL for viewing a lunch card
 */
export const generateLunchCardUrl = (cardId: string, type: 'private' | 'company'): string => {
  const basePath = type === 'private' ? '/lunchkort/visa' : '/foretagskort/visa';
  return `https://lunch-hjalpen.lovable.app${basePath}?id=${encodeURIComponent(cardId)}`;
};

/**
 * Send SMS with lunch card link
 */
export const sendLunchCardSms = async (phone: string, link: string): Promise<SendSmsResult> => {
  try {
    const response = await fetch(SMS_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ phone, link }),
    });

    if (!response.ok) {
      throw new Error(`SMS API error: ${response.status}`);
    }

    return { success: true };
  } catch (error) {
    console.error('Failed to send SMS:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

/**
 * Parse QR code data to extract card ID and type
 * Supports formats:
 * - LUNCHCARD|<id> or LUNCHCARD:<id>
 * - COMPANYCARD|<id> or COMPANYCARD:<id>
 */
export const parseQRData = (qrData: string): { id: string; type: 'private' | 'company' } | null => {
  const trimmed = qrData.trim();
  
  // Check for company card format (both | and : as separators)
  if (trimmed.startsWith('COMPANYCARD|') || trimmed.startsWith('COMPANYCARD:')) {
    const id = trimmed.replace(/^COMPANYCARD[|:]/, '').trim();
    if (id) {
      return { id, type: 'company' };
    }
  }

  // Check for lunch card format (both | and : as separators)
  if (trimmed.startsWith('LUNCHCARD|') || trimmed.startsWith('LUNCHCARD:')) {
    const id = trimmed.replace(/^LUNCHCARD[|:]/, '').trim();
    if (id) {
      return { id, type: 'private' };
    }
  }


  return null;
};

/**
 * Decrement a lunch card by ID via API
 */
export const decrementLunchCard = async (id: string): Promise<LunchCardAPI | null> => {
  try {
    const user = auth.currentUser;
    const token = user ? await user.getIdToken() : '';
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(DECREMENT_CARD_API, {
      method: 'POST',
      headers,
      body: JSON.stringify({ id }),
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();
    
    return {
      ...data,
      remaining: typeof data.remaining === 'string' ? parseInt(data.remaining, 10) : data.remaining,
    };
  } catch (error) {
    console.error('Failed to decrement lunch card:', error);
    throw error;
  }
};

